/* eslint-disable camelcase */
import BaseBreadcrumbs from "$/components/BaseBreadcrumbs";
import { getCreationBucket } from "$/util/creation_util";
import { getImageUrl, useCreation } from "$/util/db";
import { useRouteParams } from "$/util/router";
import base58 from "@blackink/core/base58";
import constants from "@blackink/core/categoryConstants";
import { titleize, trimEllip } from "@blackink/core/creation/util";
import {
  SIZES_BY_PLACEMENT,
  TempTattooProductData,
  getProductData,
  getTattooSavings,
} from "@blackink/core/productList";
import { Deblur, TripOrigin } from "@mui/icons-material";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ShareIcon from "@mui/icons-material/Share";
import {
  Box,
  Button,
  Chip,
  Container,
  FormControl,
  Grid,
  NativeSelect,
  OutlinedInput,
  Snackbar,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CirclePicker } from "react-color";
import { Helmet } from "react-helmet-async";
import { useShoppingCart } from "use-shopping-cart";
import analytics from "../util/analytics";
import CreationDetailSelector from "./CreationDetailSelector";
import CreationDetailSingleSkeleton from "./CreationDetailSingleSkeleton";
import FavoriteButton from "./FavoriteButton";
import ImageCanvasSimple from "./ImageCanvasSimple";
import Meta from "./Meta";
import Section from "./Section";

function CreationDetailSingleSection(props) {
  const { shortId, idx: _idx } = props;
  const idx = Number(_idx || 0); // Ensure idx is a int, not string
  const creationId = base58.decode(shortId);

  const theme = useTheme();
  const params: {
    size?: string;
    quantity?: string;
    pattern?: string;
    level?: string;
  } = useRouteParams();
  const { addItem, handleCartClick } = useShoppingCart();
  const { data: item } = useCreation(creationId);

  const [clipboard, setClipboard] = useState(false);
  const [tattooSize, setTattooSize] = useState(params.size || "tattoo_3_3");
  const [shadingPattern, setShadingPattern] = useState(params.pattern || "simple");
  const [bgColor, setBgColor] = useState("#F4F4F4");
  const [threshold, setThreshold] = useState(Number(params.level) || 0.25);
  const product = getProductData(tattooSize);
  const [quantity, setQuantity] = useState(Number(params.quantity) || 1);
  const [imgDataUrl, setImgDataUrl] = useState(null);

  useEffect(() => {
    // Update tattoo size when the body part changes
    if (
      item &&
      item.bodyPart &&
      SIZES_BY_PLACEMENT[item.bodyPart] &&
      SIZES_BY_PLACEMENT[item.bodyPart][0] !== tattooSize &&
      params.size === undefined
    ) {
      setTattooSize(SIZES_BY_PLACEMENT[item.bodyPart][0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  if (!shortId || !item || !item.id) {
    return <CreationDetailSingleSkeleton />;
  }

  const copy = () => {
    analytics.track("share", { type: "sharing-link", creationId, idx });
    navigator.clipboard.writeText(
      `${window.location.protocol}//${window.location.host}${window.location.pathname}`,
    );
    setClipboard(true);
  };

  const addToCart = () => {
    analytics.track("add-to-cart", { creationId, idx, tattooSize, quantity });

    const { id, width, height, price } = product;

    const image = getImageUrl(item.imagePaths[idx], getCreationBucket(item));

    addItem(
      {
        name: `${width}x${height} Temp Tattoo`,
        id: `${creationId}-${idx}-${tattooSize}-${shadingPattern}-${threshold}`,
        price_id: id,
        price,
        currency: "USD",
        image,
        url: window.location.pathname,
      },
      {
        count: quantity,
        product_metadata: {
          name: item.prompt,
          creationId: item.id,
          idx,
          image,
          tattooSize,
          shadingPattern,
          shadingIntensity: threshold,
        } as TempTattooProductData,
      },
    );
    handleCartClick();
  };

  let socialImg = null;
  if (item.imagePaths && item.imagePaths.length > 0) {
    socialImg = getImageUrl(item.imagePaths[idx], getCreationBucket(item));
  }

  return (
    <>
      <Meta
        title={`${titleize(trimEllip(item.prompt, 40))} Temporary Tattoo`}
        description={`Semi-permanent tattoo of "${titleize(
          trimEllip(item.prompt, 40),
        )}. Create designs in seconds and get temporary tattoos delivered to your door.`}
        keywords={`${titleize(
          trimEllip(item.prompt, 40),
        ).toLowerCase()} temporary tattoo, semi-permanent tattoo`}
        image={imgDataUrl}
        disableCanonicalSearch
      />
      <Helmet>
        <script type="application/ld+json" key="json-ld-shop">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            sku: item.id + (isNaN(idx) ? "" : "_" + idx),
            name: `${titleize(trimEllip(item.prompt, 100))} Temporary Tattoo`,
            description:
              "See what a real tattoo will look like before you get one! Our Serious Temporary Tattoos are safe*, easy to apply, and made of high quality materials. They last about 2 weeks (10-16 days) and are water resistant.",
            brand: {
              "@type": "Brand",
              name: "BlackInk.AI",
            },
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: `${parseFloat(
                String(getProductData(tattooSize).price / 100),
              ).toFixed(2)}`,
              url: window.location.href,
              itemCondition: "https://schema.org/NewCondition",
              availability: product.available
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            },
            google_product_category: "3509",
            image: socialImg,
          })}
        </script>
      </Helmet>
      <Section>
        <Container>
          <BaseBreadcrumbs
            categoryPath="/shop/temporary-tattoos/"
            categoryName="Temporary Tattoos"
            subCategoryName={constants.CATEGORY_DESC[item.style].name}
            subCategoryPath={`/shop/temporary-tattoos/${
              constants.CATEGORY_DESC[item.style].slug
            }`}
            title={titleize(trimEllip(item.prompt, 100))}
          />
          <Grid container columnSpacing={4}>
            <Grid item md={6} xs={12}>
              <Box sx={{ backgroundColor: bgColor, borderRadius: 4, mb: 2 }}>
                <ImageCanvasSimple
                  key="original"
                  imageUrl={socialImg}
                  width={
                    (item && item.params && item.params.width
                      ? item.params.width
                      : 512) * 2
                  }
                  height={
                    (item && item.params && item.params.height
                      ? item.params.height
                      : 512) * 2
                  }
                  blurRadius={0.5}
                  shadingPattern={shadingPattern}
                  threshold={threshold}
                  onImageDataUrl={(data) => setImgDataUrl(data)}
                />
              </Box>
              <CirclePicker
                disableAlpha
                circleSize={24}
                circleSpacing={8}
                width="100%"
                color={bgColor}
                colors={[
                  "#F4F4F4",
                  "#FADCBC",
                  "#E0BB95",
                  "#BF8F68",
                  "#9B633D",
                  "#594539",
                ]}
                onChange={(val) => {
                  setBgColor(val.hex);
                }}
              />
              <Stack
                direction="row"
                spacing={1}
                justifyContent="space-between"
                sx={{ my: 2 }}
              >
                {item.style.map((style) => (
                  <Chip
                    key={style}
                    size="medium"
                    label={constants.CATEGORY_DESC[item.style].name}
                  />
                ))}

                <Button endIcon={<ShareIcon />} onClick={copy}>
                  Sharing Link
                </Button>
              </Stack>
            </Grid>
            <Grid item md={6} xs={12}>
              <Typography
                variant="h4"
                variantMapping={{ h4: "h1" }}
                sx={{ textTransform: "capitalize", mt: 2 }}
              >
                {titleize(trimEllip(item.prompt, 100))} Temporary Tattoo
              </Typography>

              <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>
                ${getProductData(tattooSize).price / 100} USD
              </Typography>
              <Typography variant="body1" color="primary">
                Buy 4+ tattoos and get free shipping!
              </Typography>
              <Typography variant="body1" my={2}>
                See what a real tattoo will look like before you get one! Our
                <i>Serious Temporary Tattoos</i> are safe*, easy to apply, and made of
                high quality materials. They last about 2 weeks (10-16 days) and are
                water resistant.
              </Typography>
              <Typography variant="h6" my={2}>
                Style Options
              </Typography>
              <CreationDetailSelector
                item={item}
                selected={idx}
                disableBlur
                height={50}
                imageProps={{
                  sx: { filter: "grayscale(100%) contrast(1)" },
                }}
              />
              <Typography variant="h6" mt={2}>
                Shading
              </Typography>
              <Typography variant="body1" mb={1}>
                Customize the intensity and style of black ink shading in your temporary
                tattoo.
              </Typography>

              <Stack
                direction={{
                  xs: "column",
                  sm: "row",
                }}
                spacing={2}
              >
                <Box>
                  <Typography variant="subtitle3" display="block">
                    Level
                  </Typography>
                  <ToggleButtonGroup
                    value={threshold}
                    onChange={(event, val) => {
                      if (val !== null) {
                        setThreshold(val);
                      }
                    }}
                    exclusive
                    size="small"
                    aria-label="Shading Level"
                    color="primary"
                  >
                    {[
                      {
                        id: 0.35,
                        label: "Light",
                      },
                      {
                        id: 0.25,
                        label: "Medium",
                      },
                      {
                        id: 0.2,
                        label: "Heavy",
                      },
                    ].map((val) => (
                      <ToggleButton key={val.id} value={val.id} aria-label={val.label}>
                        {val.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box>
                  <Typography variant="subtitle3" display="block">
                    Pattern
                  </Typography>
                  <ToggleButtonGroup
                    value={shadingPattern}
                    onChange={(event, val) => {
                      if (val !== null) {
                        setShadingPattern(val);
                      }
                    }}
                    exclusive
                    color="primary"
                    size="small"
                    aria-label="Shading Pattern"
                  >
                    {[
                      {
                        id: "simple",
                        icon: <TripOrigin />,
                      },
                      {
                        id: "dotwork",
                        icon: <Deblur />,
                      },
                      // {
                      //   id: "etching",
                      //   icon: <Tonality />,
                      // },
                    ].map((pattern) => (
                      <ToggleButton
                        key={pattern.id}
                        value={pattern.id}
                        aria-label={`Shading Pattern ${pattern.id}`}
                      >
                        {pattern.icon}
                        {pattern.id}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              </Stack>
              <Typography variant="h6" mb={1} mt={2}>
                Tattoo Canvas Size **
              </Typography>
              <ToggleButtonGroup
                value={tattooSize}
                exclusive
                onChange={(event, val) => {
                  if (val !== null) {
                    setTattooSize(val);
                  }
                }}
                color="primary"
                size="small"
                aria-label="Tattoo Size"
              >
                {SIZES_BY_PLACEMENT[item.bodyPart]
                  .map((size) => getProductData(size))
                  .map((product) => (
                    <ToggleButton
                      key={product.id}
                      value={product.id}
                      aria-label={`Tattoo Size ${product.width}x${product.height}`}
                      sx={{ px: 2 }}
                    >
                      {`${product.width} x ${product.height}`} in
                    </ToggleButton>
                  ))}
              </ToggleButtonGroup>
              <Typography variant="h6" sx={{ mb: 1, mt: 4 }}>
                Quantity
              </Typography>
              <Stack spacing={2} direction="row">
                <FormControl>
                  <NativeSelect
                    variant="filled"
                    value={quantity}
                    input={
                      <OutlinedInput
                        notched={false}
                        size="small"
                        style={{ width: 180 }}
                      />
                    }
                    inputProps={{
                      name: "quantity",
                      id: "quantity-native",
                    }}
                    onChange={(event) => {
                      setQuantity(Number(event.target.value));
                    }}
                  >
                    {Array.from(Array(25).keys()).map((i) => (
                      <option key={i} value={i + 1}>
                        {i === 3 ? "4 (Most Popular)" : i + 1}
                      </option>
                    ))}
                  </NativeSelect>
                </FormControl>
                <Typography
                  variant="body1"
                  sx={{ mt: 1 }}
                  color={theme.palette.primary.main}
                >
                  {quantity > 1 ? (
                    <>
                      Save {getTattooSavings(quantity) * 100}% when you
                      <br />
                      buy {quantity} or more
                    </>
                  ) : (
                    <>
                      Save up to {getTattooSavings(10) * 100}% <br />
                      when you buy more!
                    </>
                  )}
                </Typography>
              </Stack>

              <Stack spacing={2} direction="row" sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{ mt: 4 }}
                  startIcon={product.available ? <AddShoppingCartIcon /> : null}
                  onClick={addToCart}
                  disabled={!product.available}
                >
                  {product.available ? "Add to Cart" : "Out of Stock"}
                </Button>
                <FavoriteButton creationId={creationId} idx={idx} full hideCount />
              </Stack>
              <Typography variant="h6" sx={{ mt: 4 }}>
                Care Instructions
              </Typography>
              <Typography variant="body1" sx={{ mt: 2 }}>
                Clean and dry skin. Apply primer gel to skin and wait 15 minutes. Cut,
                peel, and apply tattoo to skin. Leave on for 1-2 hours depending on
                desired darkness. Tattoo will darken over 24 hours.
              </Typography>
              <Typography variant="body2" sx={{ mt: 4, fontSize: 12 }}>
                * Our ink is made of Jagua fruit extract which is an edible fruit hence
                it is safe to use. Our tattoo is also made of FDA approved materials and
                it is alcohol, PPD & latex free. We recommend it for 18+ only.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: 12 }}>
                ** We adjust the original design to fit your selected tattoo size. Size
                may be smaller than canvas size. Colors and imagery are adjusted and
                small details may be removed to ensure the best quality tattoo.
              </Typography>
              <Snackbar
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                open={clipboard}
                message="Copied to clipboard!"
                autoHideDuration={5000}
                onClose={() => setClipboard(false)}
              />
            </Grid>
          </Grid>
        </Container>
      </Section>
    </>
  );
}

export default CreationDetailSingleSection;
